import {
  AnyContent,
  ContentItem,
  Feed,
  INTERACTION_EVENT_OPEN,
  RangeFilter
} from 'interfaces'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useMatch, useNavigate } from 'react-router'
import { Subject, from, of } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { CONTENT_TYPE_TO_URL_SEGMENT_MAP } from 'services/content/adapters/mappings'
import { addRecentSource, setFeedToSave as setSaveFeed, trackFeedInteraction } from 'services/content/recommended/actions'
import { userFeedsSelector } from 'services/content/selectors'
import { StoreThunkDispatch } from 'store/state'
import * as FilterStore from 'utils/filter-store'
import FeedBricksView from './FeedBricksView'
import styles from './FeedView.pcss'
import FeedViewHeader from './FeedViewHeader'
import { ContentType, FilterType } from 'shared'

export interface FeedViewProps {
  feed: Feed
  range: RangeFilter
  defaultFilter: FilterType
  basePath: string
  itemWidth?: number
  withHeader?: boolean
  linkToUserFeedId?: boolean
  skipContentFeatureChecks?: boolean
  onSaveFeed?(feed: Feed): boolean
  onItemPinned?(feed: Feed, content: AnyContent): void
  onItemShared?(feed: Feed, content: AnyContent): void | boolean
  onFilterChanged?(feed: Feed, filter: FilterType): void
  onGotoClicked?(feed: Feed, content: AnyContent, by: string): void
  onRangeChanged?(feed: Feed, range: RangeFilter): void
  onContentItemClick?(item: ContentItem): void
}

export const FeedView = ({
  feed,
  range,
  defaultFilter,
  basePath,
  itemWidth,
  withHeader,
  linkToUserFeedId,
  skipContentFeatureChecks,
  onSaveFeed,
  onItemPinned,
  onItemShared,
  onFilterChanged,
  onGotoClicked,
  onRangeChanged,
  onContentItemClick
}: FeedViewProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const match = useMatch(`${basePath}/:id/:type/:range`)
  const dispatch = useDispatch<StoreThunkDispatch>()
  const userFeeds = useSelector(userFeedsSelector)

  const trackInteraction$ = useRef<Subject<string>>(undefined)

  // Initialize tracking interaction subject
  useEffect(() => {
    trackInteraction$.current = new Subject()
    const subscription = trackInteraction$.current
      .pipe(
        mergeMap((id: string) => {
          return from(dispatch(trackFeedInteraction({ feedIds: [id], event: INTERACTION_EVENT_OPEN })).unwrap())
            .pipe(catchError(() => of({ error: true })))
        })
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
      trackInteraction$.current?.unsubscribe()
    }
  }, [dispatch])

  // Check if feed is owned by current user
  const isFeedOwned = useMemo(() => {
    const { id, uniqueSource } = feed
    return userFeeds.some((userFeed: any) => userFeed.uniqueSource === uniqueSource || userFeed.id === id)
  }, [feed, userFeeds])

  // Set feed filters in store
  useEffect(() => {
    FilterStore.setFeedFilters(feed, defaultFilter, range)
  }, [defaultFilter, feed.id, range, feed])

  // Track interaction and add to recent sources
  useEffect(() => {
    trackInteraction$.current?.next(feed.uniqueSource)
    dispatch(addRecentSource(feed))
  }, [feed.id, feed.uniqueSource, dispatch, feed])

  const navigateTo = useCallback((filter?: FilterType, newRange?: RangeFilter) => {
    const feedId = linkToUserFeedId ? feed.id : feed.uniqueSource
    const filterSlug = filter ? CONTENT_TYPE_TO_URL_SEGMENT_MAP[filter] : match?.params.type
    const rangeSlug = newRange || match?.params.range
    const nextPath = match?.pattern.path
      ?.replace(':id', feedId)
      ?.replace(':type', filterSlug || '')
      ?.replace(':range', rangeSlug || '')

    const { state, search } = location

    setTimeout(() => {
      navigate(nextPath + search, { state, replace: true })
    })
  }, [feed.id, feed.uniqueSource, linkToUserFeedId, match, location, navigate])

  const handleRangeChanged = useCallback((changedFeed: Feed, changedRange: RangeFilter) => {
    navigateTo(undefined, changedRange)

    if (onRangeChanged) {
      onRangeChanged(changedFeed, changedRange)
    }
  }, [navigateTo, onRangeChanged])

  const handleFilterChange = useCallback((filter: ContentType) => {
    navigateTo(filter)
    if (onFilterChanged) {
      onFilterChanged(feed, filter)
    }
  }, [navigateTo, feed, onFilterChanged])

  const handleSaveFeed = useCallback(() => {
    const preventDefault = onSaveFeed && onSaveFeed(feed)
    if (!preventDefault) {
      dispatch(setSaveFeed(feed))
    }
  }, [feed, onSaveFeed, dispatch])

  const handleSelectedSourceChange = useCallback((sourceUrl: string) => {
    navigate(sourceUrl)
  }, [navigate])

  // Determine current filter
  const currentFilter = useMemo(() => {
    return feed.sources.find(s => s === defaultFilter) ? defaultFilter : feed.sources[0]
  }, [feed.sources, defaultFilter])

  return (
    <section className={styles.folder}>
      {withHeader && (
        <FeedViewHeader
          feed={feed}
          onSave={handleSaveFeed}
          onSourceChange={handleSelectedSourceChange}
        />
      )}
      <FeedBricksView
        filter={currentFilter}
        feed={feed}
        itemWidth={itemWidth}
        range={range}
        skipContentFeatureChecks={skipContentFeatureChecks}
        onItemPinned={onItemPinned}
        onItemShared={onItemShared}
        onRangeChanged={handleRangeChanged}
        onFilterChange={handleFilterChange}
        onGotoClicked={onGotoClicked}
        onContentItemClick={onContentItemClick}
      />
    </section>
  )
}

export default FeedView
